import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";

import { Button } from "@/components/ui/Button";
import { authApi } from "@/lib/api/auth";
import { queryClient } from "@/lib/query-client";
import { notify } from "@/lib/notify";
import {
  ACTIVITY_KEY,
  HEARTBEAT_KEY,
  IDLE_SIGNOUT_KEY,
  IDLE_SIGN_OUT_MESSAGE,
  announceIdleSignOut,
  clearStamps,
  describeWindow,
  formatCountdown,
  heartbeatIntervalMs,
  idlePhase,
  readStamp,
  remainingMs,
  writeStamp,
} from "@/lib/session/idleSession";
import { useAuthStore } from "@/stores/auth-store";

/** Input that counts as "the person is using the app". Scroll and mouse move
 * are included on purpose: reading a long page is activity. */
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "wheel", "touchstart", "click"] as const;

/** Clock ticks once a second: the figure on screen and the timeout decision. */
const TICK_MS = 1000;
/** Events closer together than this are one activity, not many. */
const ACTIVITY_THROTTLE_MS = 1000;

interface Policy {
  idleMs: number;
  warnMs: number;
}

/**
 * Signs a browser session out after a period of inactivity and warns first.
 *
 * Renders nothing until the server confirms the rule applies to this session.
 * Then it (1) records activity (also across tabs, via localStorage), (2)
 * reports it to the server in a throttled heartbeat, which is the only thing
 * that moves the server's clock, (3) opens a "Continue Session" prompt when the
 * warning window starts, and (4) signs out when the time is up. Background
 * traffic (polling, token refresh) is deliberately not activity.
 */
export function InactivityGuard() {
  const hasSession = useAuthStore((s) => !!s.refreshToken);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [leftMs, setLeftMs] = useState<number | null>(null); // set only while the warning is up
  const [continuing, setContinuing] = useState(false);

  const lastActivity = useRef(0);
  const warned = useRef(false);
  const ended = useRef(false);
  const policyRef = useRef<Policy | null>(null);

  // Learn the rule and the server's view of this session. This is also the
  // start-up check: a session that already sat idle too long comes back as a
  // 401 here, which the API client turns into a sign-out with the right message.
  useEffect(() => {
    if (!hasSession) {
      policyRef.current = null;
      setPolicy(null);
      return;
    }
    let cancelled = false;
    ended.current = false;
    authApi
      .sessionStatus()
      .then((status) => {
        if (cancelled || !status.enforced) return;
        const idleMs = (status.idle_timeout_seconds ?? 0) * 1000;
        const warnMs = (status.warning_seconds ?? 0) * 1000;
        if (idleMs <= 0) return;
        const now = Date.now();
        const serverLast = now - (idleMs - (status.remaining_seconds ?? 0) * 1000);
        // Another tab may know of more recent activity than the server has
        // heard of yet; a stamp from the future (clock change) is ignored.
        const shared = readStamp(ACTIVITY_KEY);
        const known = shared !== null && shared <= now ? shared : 0;
        lastActivity.current = Math.max(serverLast, known);
        writeStamp(ACTIVITY_KEY, lastActivity.current);
        writeStamp(HEARTBEAT_KEY, Math.max(readStamp(HEARTBEAT_KEY) ?? 0, serverLast));
        warned.current = false;
        const next = { idleMs, warnMs };
        policyRef.current = next;
        setPolicy(next);
      })
      .catch(() => {
        // Not a timeout verdict: an offline start-up or a server hiccup must
        // not sign anyone out. The server still enforces on every request.
      });
    return () => {
      cancelled = true;
    };
  }, [hasSession]);

  const endSession = useCallback(async () => {
    if (ended.current) return;
    ended.current = true;
    const token = useAuthStore.getState().accessToken;
    announceIdleSignOut();
    // Best effort and deliberately outside the API client: the session is
    // probably already gone server-side, and a 401 here must not start the
    // client's own refresh-and-toast path a second time.
    if (token) {
      void axios
        .post(`${import.meta.env.VITE_API_BASE_URL}/auth/logout`, null, { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => undefined);
    }
    notify.error(IDLE_SIGN_OUT_MESSAGE);
    clearStamps();
    useAuthStore.getState().signOut();
    queryClient.clear();
  }, []);

  const heartbeat = useCallback(() => {
    writeStamp(HEARTBEAT_KEY, Date.now());
    authApi.recordActivity().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!policy) return;
    const { idleMs, warnMs } = policy;
    const beatEvery = heartbeatIntervalMs(idleMs);

    const tick = () => {
      if (ended.current) return;
      const now = Date.now();
      const shared = readStamp(ACTIVITY_KEY);
      if (shared !== null && shared > lastActivity.current && shared <= now) lastActivity.current = shared;

      const phase = idlePhase(now, lastActivity.current, idleMs, warnMs);
      if (phase === "expired") {
        void endSession();
        return;
      }
      const lastBeat = readStamp(HEARTBEAT_KEY) ?? 0;
      if (phase === "warning") {
        if (!warned.current) {
          warned.current = true;
          // Bring the server up to date before the warning appears, so its
          // deadline and the countdown on screen are the same moment.
          if (lastActivity.current > lastBeat) heartbeat();
        }
        setLeftMs(remainingMs(now, lastActivity.current, idleMs));
        return;
      }
      if (warned.current) {
        // Renewed from another tab.
        warned.current = false;
        setLeftMs(null);
      }
      if (lastActivity.current > lastBeat && now - lastBeat >= beatEvery) heartbeat();
    };

    const onActivity = () => {
      // While the warning is up only the button renews the session, so a
      // stray mouse movement cannot dismiss it unseen.
      if (warned.current || ended.current) return;
      const now = Date.now();
      if (now - lastActivity.current < ACTIVITY_THROTTLE_MS) return;
      lastActivity.current = now;
      writeStamp(ACTIVITY_KEY, now);
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === IDLE_SIGNOUT_KEY && e.newValue) {
        // Another tab of this browser timed out; the clock is shared, so this
        // one is out too.
        if (ended.current || !useAuthStore.getState().refreshToken) return;
        ended.current = true;
        useAuthStore.getState().signOut();
        queryClient.clear();
        return;
      }
      if (e.key === ACTIVITY_KEY) tick();
    };

    // Waking from sleep or returning to the tab: judge by the wall clock now,
    // not at the next timer tick.
    const onWake = () => {
      if (!document.hidden) tick();
    };

    for (const name of ACTIVITY_EVENTS) window.addEventListener(name, onActivity, { passive: true, capture: true });
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    const timer = window.setInterval(tick, TICK_MS);
    tick();
    return () => {
      for (const name of ACTIVITY_EVENTS) window.removeEventListener(name, onActivity, { capture: true });
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      window.clearInterval(timer);
    };
  }, [policy, endSession, heartbeat]);

  async function continueSession() {
    setContinuing(true);
    try {
      await authApi.recordActivity();
      const now = Date.now();
      lastActivity.current = now;
      writeStamp(ACTIVITY_KEY, now);
      writeStamp(HEARTBEAT_KEY, now);
      warned.current = false;
      setLeftMs(null);
    } catch (err) {
      // A 401 means the session really did run out; the API client has already
      // signed the user out. Anything else (offline, 5xx) leaves the prompt up.
      if (axios.isAxiosError(err) && err.response?.status === 401) return;
      if (useAuthStore.getState().refreshToken) notify.error("Could not renew the session. Please try again.");
    } finally {
      setContinuing(false);
    }
  }

  async function signOutNow() {
    ended.current = true;
    try {
      await authApi.logout();
    } catch {
      // The local session is cleared regardless.
    }
    clearStamps();
    useAuthStore.getState().signOut();
    queryClient.clear();
  }

  if (!policy || leftMs === null) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-[1px]" aria-hidden />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="idle-session-title"
          aria-describedby="idle-session-body"
          className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
        >
          <h2 id="idle-session-title" className="text-base font-semibold text-slate-900">
            Are you still there?
          </h2>
          <p id="idle-session-body" className="mt-2 text-sm text-slate-600">
            Your session will expire in {describeWindow(policy.warnMs)} due to inactivity. Would you like to continue?
          </p>
          <p className="mt-4 text-sm text-slate-500">
            Time left{" "}
            <span data-testid="idle-countdown" className="font-mono text-base font-semibold tabular-nums text-slate-900">
              {formatCountdown(leftMs)}
            </span>
          </p>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => void signOutNow()} disabled={continuing}>
              Sign out
            </Button>
            <Button autoFocus onClick={() => void continueSession()} loading={continuing}>
              Continue Session
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
